import { AdItemInterface, PlayerHookType, VideoPlayerInterface } from '@cbsinteractive/avia-js';
import { HookContextInterface } from '@cbsinteractive/avia-js/dist/types/iface/HookContextInterface';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';
import { IAviaPlayerInjectedAdHookArgument } from 'tag/ts/tag/player/adapters/avia/interfaces/arguments/IAviaPlayerInjectedAdHookArgument';
import { TIsSkippableCallback } from 'tag/ts/tag/types/TIsSkippableCallback';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Ad hook which uses callback parameter to determine if ad is skippable
 */
export class AviaPlayerInjectedAdHook extends AbstractAviaPlayerHook {

	/**
	 * @description Ad hook
	 */
	hookType: PlayerHookType = PlayerHookType.AD;

	/**
	 * @description Check if ad can be skipped
	 */
	#isSkippable: TIsSkippableCallback;

	constructor( argument: IAviaPlayerInjectedAdHookArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerInjectedAdHook'
			}
		);
		this.#isSkippable = argument?.isSkippableCallback;

		// setup tag events handler
		this.tagEventsHandler( this.#onTagEvent, [
			TagEvent.ON_CREATE_PLAYER_SUCCESS
		] );
	}

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// stop listening to this event... we need it once per instance to prevent memory leak
				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[ TagEvent.ON_CREATE_PLAYER_SUCCESS ]
				).then(
					( data ) => {
						this?.logger?.log( 'Successfully removed tag event handler' );
					}
				).catch(
					( error ) => {
						// Event handler removal failed - log but continue with ad hook registration
						this?.logger?.warn( 'Failed to remove tag event handler:', error );
					}
				).finally(
					() => {
						// Always register ad hook - critical for ad functionality regardless of cleanup result
						this.registerHook();
					}
				);
				break;
		}
	};

	/**
	 * @description Callback passed to player's registerHook function
	 * @param {HookContextInterface<AdItemInterface>} context
	 */
	callback = ( context: HookContextInterface<AdItemInterface> ): void => {
		this?.logger?.log( this.name, 'callback:', 'ad hook fired', 'context:', context );
		const adItem = context?.value as AdItemInterface;
		if ( this.#isSkippable( adItem ) ) {
			adItem.skippable = true;
			this?.logger?.log( 'set adItem to skippable:', adItem );
		}

		// inform application
		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_PLAYER_HOOK_EXECUTED,
				this.tag,
				{
					player: this?.tag?.controller?.playerManager?.player,
					adaptedPlayerInstance: this?.tag?.controller?.playerManager?.playerAdapter?.adaptedPlayerInstance as VideoPlayerInterface,
					adItem: {
						skippable: adItem?.skippable
					},
					hook: this
				},
				this,
				this.name
			)
		);
	};
}